import React, { Suspense, useState, useTransition } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { Navigate, useNavigate } from 'react-router-dom';
import { graphql } from 'relay-runtime';

import { backofficeUrl, T } from 'src/shared/helpers';
import styled from 'styled-components';
import { TextButton } from '../../../widgets/buttons';
import { EmptyList } from '../../../widgets/emptyList';
import { SelectionValue, SelectOptions } from '../../../widgets/select';
import { Row, Table, TableContentLoader, TableHeading } from '../../../widgets/table';
import { FiltersRow } from '../../../widgets/filters';

// Import atomic task components
import { TaskStatusBadge, useTaskPolling } from 'src/components/pages/taskManagement';

import type { salesChannelSwitchTasksQuery } from './__generated__/salesChannelSwitchTasksQuery.graphql';
import { formatDate, getTaskTypeLabel, SWITCH_TASKS_TAB_URL, toTaskState, toTaskStateForPolling } from './switchTaskHelpers';

const FilterLabel = styled.span`
    display: block;
    padding: 4px 0;
`;

const CellContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
`;

const ActionsCell = styled(CellContainer)`
    justify-content: flex-end;
`;

const headings: TableHeading[] = [
    [T._('Task ID', 'Admin'), '0.5fr'],
    [T._('Status', 'Admin'), '1fr'],
    [T._('Type', 'Admin'), '1fr'],
    [T._('Created', 'Admin'), '1fr'],
    [T._('Progress / Result', 'Admin'), '1.5fr'],
    [T._('Actions', 'Admin'), '1.2fr']
];

const salesChannelSwitchTasksQueryGraphQL = graphql`
    query salesChannelSwitchTasksQuery {
        productSwitcherTasks {
            taskID
            taskType
            state
            createdOn
            modifiedOn
            errorCount
            lastError
            elapsedTime
            extraData {
                taskID
                sellerID
                masterSalesChannelID
                secondarySalesChannelID
                areSwitchedSCApiKeys
                areSwitchedSCEcommerceSettings
                areSwitchedProductCodes
                switchedProducts
                areSalesChannelDetailsSwitched
                areProductMultipleVariantsRulesAttributesSwitched
                areOrdersSalesChannelsSwitched
                errors {
                    productIDs
                    orderIDs
                }
            }
        }
    }
`;

type TaskType = NonNullable<salesChannelSwitchTasksQuery['response']['productSwitcherTasks']>[number];

interface SalesChannelSwitchTasksContentProps {
    statusFilter: number | null;
    onTaskClick: (taskId: number) => void;
    onRefetch: () => void;
    refreshKey: number;
}

const SalesChannelSwitchTasksContent: React.FC<SalesChannelSwitchTasksContentProps> = ({ statusFilter, onTaskClick, onRefetch, refreshKey }) => {
    const data = useLazyLoadQuery<salesChannelSwitchTasksQuery>(salesChannelSwitchTasksQueryGraphQL, {}, { fetchPolicy: 'network-only', fetchKey: refreshKey });

    // Filter tasks client-side: only SWITCHSALESCHANNELS type, then by status
    const allTasks = (data.productSwitcherTasks || []).filter((task: any) => task.taskType === 'SWITCHSALESCHANNELS');
    const filteredTasks = statusFilter !== null ? allTasks.filter((task: any) => task.state === statusFilter) : allTasks;

    // Map tasks to the format expected by useTaskPolling.
    // Not memoized on purpose: a fresh array identity every render is what the current polling cadence depends on.
    const tasksForPolling = filteredTasks.map((task) => ({
        taskState: toTaskStateForPolling(task.state)
    }));

    // Use polling hook for auto-refresh
    useTaskPolling({
        tasks: tasksForPolling,
        onPoll: onRefetch,
        intervalMs: 5000
    });

    if (filteredTasks.length === 0) {
        return (
            <>
                <EmptyList
                    title={T._('No sales channel switch tasks found', 'Admin')}
                    subTitle={T._('Sales channel switch tasks will appear here when you switch your Master Integration.', 'Admin')}
                />
            </>
        );
    }

    const renderProgressOrResult = (task: (typeof filteredTasks)[number]) => {
        // For processing/waiting tasks, show status
        if (task.state === 0 || task.state === 1) {
            return <span>{T._('In Progress', 'Admin')}</span>;
        }

        // For completed tasks, show success
        if (task.state === 2) {
            return <span style={{ color: '#388e3c' }}>✓ {T._('Completed', 'Admin')}</span>;
        }

        // For error tasks, show error
        if (task.state === 3) {
            return <span style={{ color: '#d32f2f' }}>✗ {T._('Failed', 'Admin')}</span>;
        }

        return <span>-</span>;
    };

    return (
        <Table headings={headings}>
            {filteredTasks.map((task: any) => (
                <Row
                    key={task.taskID}
                    onClick={() => onTaskClick(task.taskID)}
                    cells={[
                        <CellContainer>#{task.taskID}</CellContainer>,
                        <CellContainer>
                            <TaskStatusBadge status={toTaskState(task.state)} />
                        </CellContainer>,
                        <CellContainer>{getTaskTypeLabel(task.taskType)}</CellContainer>,
                        <CellContainer>{formatDate(task.createdOn)}</CellContainer>,
                        <CellContainer>{renderProgressOrResult(task)}</CellContainer>,
                        <ActionsCell>
                            {/* The row already opens the task on click, so the button must not trigger it a second time. */}
                            <TextButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onTaskClick(task.taskID);
                                }}
                            >
                                {T._('Details', 'Admin')}
                            </TextButton>
                        </ActionsCell>
                    ]}
                />
            ))}
        </Table>
    );
};

/**
 * The sales channel switch task list (status filter + auto-refreshing table), rendered in the Tasks tab of the
 * Sales Channels page.
 */
export const SalesChannelSwitchTasksPanel: React.FC = () => {
    const navigate = useNavigate();
    const [statusFilter, setStatusFilter] = useState<number | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [, startPollTransition] = useTransition();

    const statusOptions = [
        { label: <FilterLabel>{T._('All', 'Admin')}</FilterLabel>, value: '' },
        { label: <FilterLabel>{T._('Processing', 'Admin')}</FilterLabel>, value: '1' },
        { label: <FilterLabel>{T._('Completed', 'Admin')}</FilterLabel>, value: '2' },
        { label: <FilterLabel>{T._('Failed', 'Admin')}</FilterLabel>, value: '3' }
    ];

    const handleStatusFilterChange = (value: SelectionValue<string>) => {
        if (!Array.isArray(value)) {
            setStatusFilter(value ? parseInt(value, 10) : null);
        }
    };

    // Marking the refetch as a transition keeps the current table on screen while the new data loads. Without it,
    // bumping the fetchKey suspends useLazyLoadQuery and the Suspense boundary swaps the table for its loader.
    const handlePollRefetch = () => startPollTransition(() => setRefreshKey((prev) => prev + 1));

    const handleTaskClick = (taskId: number) => {
        navigate(backofficeUrl(`/sales-channels/switch-tasks/${taskId}`));
    };

    return (
        <>
            <FiltersRow>
                <SelectOptions
                    placeholder={T._('Status', 'Admin')}
                    multiple={false}
                    options={statusOptions}
                    onChange={handleStatusFilterChange}
                    width={'200px'}
                />
            </FiltersRow>

            {/* No key={refreshKey} here on purpose: remounting on every poll would rebuild the whole table's DOM,
                losing scroll position and closing any open row menu. The refetch is driven by fetchKey instead, so
                React reconciles the rows in place. The Suspense fallback is only for the very first load. */}
            <Suspense fallback={<TableContentLoader />}>
                <SalesChannelSwitchTasksContent
                    statusFilter={statusFilter}
                    onTaskClick={handleTaskClick}
                    onRefetch={handlePollRefetch}
                    refreshKey={refreshKey}
                />
            </Suspense>
        </>
    );
};

/** The task list moved into the Sales Channels page: the old URL now lands on the Tasks tab. */
const SalesChannelSwitchTasks: React.FC = () => <Navigate replace to={backofficeUrl(SWITCH_TASKS_TAB_URL)} />;

export default SalesChannelSwitchTasks;
